import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { Icon } from "@iconify/react";
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth";
import AuthContext from "../../contexts/AuthContext";
import { useToast } from "../../design-system/contexts/ToastContext";
import { setJwtToken } from "../../axios";
import VerifyOtpSignup from "../../components/pages/signup";
import { isValidEmail, parseAuthError } from "../../utils/validation";

// Auth Components
import { AuthLayout, SocialLoginButtons, OrDivider } from "./components";
import "./Auth.css";

const SignIn = ({ user }) => {
  const query = new URLSearchParams(window.location.search);
  const domain = query.get("redirect");
  const { setValue } = useContext(AuthContext);
  const { setEventUpdate } = useContext(AuthContext);
  const { success, error: showError } = useToast();

  const navigate = useNavigate();

  const [state, setState] = useState({
    data: {
      email: user.emailId || "",
      password: "",
      inputOtp: "",
    },
    loading: false,
    sendingEmail: false,
    userNotConfirmed: false,
    receivedOtp: false,
    errors: {
      email: "",
      password: "",
      inputOtp: "",
    },
    redirect: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  const doValidate = () => {
    if (state.data.email === undefined || state.data.email === "") {
      let _state = state;
      _state.errors = {
        email: "Please enter your email",
        password: _state.errors.password,
      };
      setState({ ...state, ..._state });
      return false;
    }

    // Validate email format
    if (!isValidEmail(state.data.email)) {
      let _state = state;
      _state.errors = {
        email: "Please enter a valid email address",
        password: _state.errors.password,
      };
      setState({ ...state, ..._state });
      return false;
    }

    if (state.data.password === undefined || state.data.password === "") {
      let _state = state;
      _state.errors = {
        email: _state.errors.email,
        password: "Please enter your password",
      };
      setState({ ...state, ..._state });
      return false;
    }
    return true;
  };

  const doFederatedSignIn = async (provider) => {
    if (provider === "LinkedIn") {
      const generateState = () => {
        return Math.random().toString(36).substring(2, 15) +
               Math.random().toString(36).substring(2, 15);
      };
      const clientId = "77t75blts25aq1";
      const redirectUri = "/";

      const state = generateState();
      localStorage.setItem("oauth_state", state);

      const scope = encodeURIComponent("r_emailaddress r_liteprofile");
      const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;

      window.location.href = linkedInAuthUrl;
    }
    await Auth.federatedSignIn({ provider });
  };

  const signIn = async () => {
    if (doValidate()) {
      setState({ ...state, loading: true });
      try {
        const user = await Auth.signIn(state.data.email, state.data.password);
        setJwtToken(user.signInUserSession.idToken.jwtToken);
        success("Signed In successfully");
        navigate(0);
      } catch (err) {
        let error = parseAuthError(err);
        if (error.includes("verification is incomplete") || error.includes("not confirmed")) {
          error = "Your account verification is incomplete. Please complete the verification before logging in.";
          setState({ ...state, userNotConfirmed: true, loading: false });
          showError(error);
        } else if (error.includes("does not exist") || error.includes("could not find")) {
          error = "We could not find your login details. If you have signed up earlier using social log in, then please follow the same.";
          showError(error);
          setState({ ...state, loading: false });
        } else {
          showError(error);
          setState({ ...state, loading: false });
        }
      }
    }
  };

  const handlePressEnter = (e) => {
    if (e.keyCode === 13) {
      signIn();
    }
  };

  const resendConfirmationLink = async () => {
    setState({ ...state, sendingEmail: true });
    let error = "";
    if (state.data.email === undefined || state.data.email === "")
      error = "Please enter your email";
    if (!isValidEmail(state.data.email))
      error = "Please enter a valid email address";

    if (error === "") {
      try {
        await Auth.resendSignUp(state.data.email);
        success("Verification code resent successfully. Please verify your account by providing the code.");
        setState({
          ...state,
          userNotConfirmed: true,
          sendingEmail: false,
          receivedOtp: true,
        });
      } catch (err) {
        let errorMsg = parseAuthError(err);
        showError(errorMsg);
        setState({
          ...state,
          userNotConfirmed: false,
          sendingEmail: false,
          receivedOtp: false,
        });
      }
    } else {
      showError(error);
      setState({ ...state, sendingEmail: false, receivedOtp: false });
    }
  };

  if (state.receivedOtp) {
    return (
      <VerifyOtpSignup
        state={state}
        setState={setState}
        user={""}
        signUp={resendConfirmationLink}
      />
    );
  }

  return (
    <AuthLayout>
      {/* Social Login */}
      <SocialLoginButtons
        onGoogleClick={() => doFederatedSignIn(CognitoHostedUIIdentityProvider.Google)}
        onAmazonClick={() => doFederatedSignIn(CognitoHostedUIIdentityProvider.Amazon)}
        onLinkedInClick={() => doFederatedSignIn("LinkedIn")}
        showGoogleFull={true}
      />

      <OrDivider />

      {/* Email Input */}
      <div className="auth-form__group">
        <label className="auth-form__label">Email</label>
        <input
          type="email"
          className={`auth-form__input ${state.errors.email ? "auth-form__input--error" : ""}`}
          placeholder="johnd@gmail.com"
          value={state.data.email}
          onKeyDown={handlePressEnter}
          onChange={(e) => {
            setState({
              ...state,
              data: {
                ...state.data,
                email: e.target.value.toLowerCase(),
              },
              errors: {
                ...state.errors,
                email: "",
              },
            });
          }}
          onBlur={() => {
            if (state.data.email === undefined || state.data.email === "") {
              setState({
                ...state,
                errors: {
                  ...state.errors,
                  email: "Please enter your email",
                },
              });
            } else if (!isValidEmail(state.data.email)) {
              setState({
                ...state,
                errors: {
                  ...state.errors,
                  email: "Please enter a valid email address",
                },
              });
            }
          }}
        />
        {state.errors.email && (
          <span className="auth-form__error">{state.errors.email}</span>
        )}
      </div>

      {/* Password Input */}
      <div className="auth-form__group">
        <label className="auth-form__label">Password</label>
        <div className="auth-form__password-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            className={`auth-form__input ${state.errors.password ? "auth-form__input--error" : ""}`}
            placeholder="Password"
            value={state.data.password}
            onKeyDown={handlePressEnter}
            onChange={(e) => {
              setState({
                ...state,
                data: {
                  ...state.data,
                  password: e.target.value,
                },
                errors: {
                  ...state.errors,
                  password: "",
                },
              });
            }}
            onBlur={() => {
              if (state.data.password === undefined || state.data.password === "") {
                setState({
                  ...state,
                  errors: {
                    ...state.errors,
                    password: "Please enter your password",
                  },
                });
              }
            }}
          />
          <button
            type="button"
            className="auth-form__password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <Icon
              icon={showPassword ? "mdi-light:eye" : "mdi-light:eye-off"}
              width="20"
              height="20"
            />
          </button>
        </div>
        {state.errors.password && (
          <span className="auth-form__error">{state.errors.password}</span>
        )}
      </div>

      {/* Forgot Password */}
      <Link
        className="auth-link auth-link--right"
        to={domain ? `/forgot-password?redirect=${domain}` : `/forgot-password`}
      >
        Forgot Password?
      </Link>

      {/* Unconfirmed User Alert */}
      {state.userNotConfirmed && (
        <div className="auth-alert auth-alert--info">
          {state.sendingEmail ? (
            <>
              <span className="auth-spinner"></span> Sending confirmation mail.
            </>
          ) : (
            <>
              Looks like your account is not confirmed.{" "}
              <span
                className="auth-link"
                onClick={resendConfirmationLink}
              >
                Click Here
              </span>{" "}
              to re-send confirmation code.
            </>
          )}
        </div>
      )}

      {/* Sign In Button */}
      <button
        className="auth-btn"
        onClick={signIn}
        disabled={state.loading}
        style={{ marginTop: "var(--spacing-4)" }}
      >
        {state.loading ? <span className="auth-spinner"></span> : "Sign In"}
      </button>

      {/* Sign In with OTP */}
      <p className="auth-footer-text" style={{ marginTop: "var(--spacing-4)" }}>
        Sign In with{" "}
        <span
          role="button"
          onClick={() => navigate(`/request-otp`, { state: { signIn: true } })}
        >
          Email OTP
        </span>
      </p>

      {/* Sign Up Link */}
      <p className="auth-footer-text">
        Don't have an account?{" "}
        <span
          role="button"
          onClick={() => {
            if (domain) {
              navigate(`/signup?redirect=${domain}`);
            } else {
              navigate("/signup");
            }
          }}
        >
          Sign Up
        </span>
      </p>
    </AuthLayout>
  );
};

export default SignIn;
